import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { submitDocFeedback, getDocFeedback, updateFeedbackStatus, resolveDocFeedback, getSuggestChangesStatus, applyDocContent } from '../services/api';
import { toast } from 'sonner';
import {
  X, MessageSquarePlus, CheckCircle2, AlertCircle, HelpCircle,
  ThumbsUp, Send, Loader2, ChevronDown, Clock, CheckCheck, Sparkles
} from 'lucide-react';

const FEEDBACK_TYPES = [
  { value: 'inaccurate', label: 'Inaccurate', icon: AlertCircle, color: 'text-red-500', bg: 'bg-red-50 border-red-200', badge: 'bg-red-100 text-red-700' },
  { value: 'missing', label: 'Missing Info', icon: HelpCircle, color: 'text-amber-500', bg: 'bg-amber-50 border-amber-200', badge: 'bg-amber-100 text-amber-700' },
  { value: 'unclear', label: 'Unclear', icon: MessageSquarePlus, color: 'text-orange-500', bg: 'bg-orange-50 border-orange-200', badge: 'bg-orange-100 text-orange-700' },
  { value: 'good', label: 'Good', icon: ThumbsUp, color: 'text-emerald-500', bg: 'bg-emerald-50 border-emerald-200', badge: 'bg-emerald-100 text-emerald-700' },
  { value: 'general', label: 'General', icon: MessageSquarePlus, color: 'text-slate-500', bg: 'bg-slate-50 border-slate-200', badge: 'bg-slate-100 text-slate-700' },
];

const STATUS_MAP = {
  open: { label: 'Open', icon: Clock, color: 'text-blue-600', bg: 'bg-blue-50' },
  acknowledged: { label: 'Acknowledged', icon: CheckCircle2, color: 'text-amber-600', bg: 'bg-amber-50' },
  resolved: { label: 'Resolved', icon: CheckCheck, color: 'text-emerald-600', bg: 'bg-emerald-50' },
};

const FeedbackPanel = ({ docId, docContent, docVersion, isOpen, onClose, initialSection, onFeedbackCountChange, onApplied }) => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [proposal, setProposal] = useState(null);
  const [applying, setApplying] = useState(false);
  const pollRef = React.useRef(null);
  const [showForm, setShowForm] = useState(false);
  const [sectionHeading, setSectionHeading] = useState('');
  const [feedbackType, setFeedbackType] = useState('general');
  const [comment, setComment] = useState('');
  const [typeDropdownOpen, setTypeDropdownOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');

  // Clean up any in-flight polling on unmount
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const handleResolveWithAI = async () => {
    setResolving(true);
    setProposal(null);
    try {
      const res = await resolveDocFeedback(docId);
      const jobId = res.data.job_id;
      toast.success(`AI is drafting a revision addressing ${res.data.feedback_count} open feedback item(s)…`);
      pollRef.current = setInterval(async () => {
        try {
          const st = await getSuggestChangesStatus(jobId);
          const status = st.data.status;
          if (status === 'completed' || status === 'complete') {
            clearInterval(pollRef.current);
            setResolving(false);
            setProposal(st.data.result || null);
            if (!st.data.result) toast.error('AI produced no proposal');
          } else if (status === 'failed') {
            clearInterval(pollRef.current);
            setResolving(false);
            toast.error(st.data.error || 'AI drafting failed');
          }
        } catch { /* transient poll error — keep polling */ }
      }, 3000);
    } catch (err) {
      setResolving(false);
      toast.error(err.response?.data?.detail || 'Failed to start AI resolution');
    }
  };

  const handleApplyProposal = async () => {
    if (!proposal?.suggested_content) return;
    setApplying(true);
    try {
      await applyDocContent(docId, proposal.suggested_content, docVersion);
      toast.success('Revision applied — remember to mark the feedback items resolved');
      setProposal(null);
      if (onApplied) onApplied();
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error('Document changed since the proposal was drafted — refresh and try again');
      } else {
        toast.error(err.response?.data?.detail || 'Failed to apply revision');
      }
    } finally {
      setApplying(false);
    }
  };

  // Extract section headings from doc content for the dropdown
  const sectionHeadings = React.useMemo(() => {
    if (!docContent) return [];
    const headings = [];
    const lines = docContent.split('\n');
    for (const line of lines) {
      const match = line.match(/^(#{1,3})\s+(.+)/);
      if (match) {
        const text = match[2].replace(/\*\*/g, '').replace(/\*/g, '').trim();
        headings.push(text);
      }
    }
    return headings;
  }, [docContent]);

  const fetchFeedback = useCallback(async () => {
    if (!docId) return;
    setLoading(true);
    try {
      const res = await getDocFeedback(docId);
      setFeedbacks(res.data);
      const openCount = res.data.filter(f => f.status === 'open').length;
      if (onFeedbackCountChange) onFeedbackCountChange(openCount, res.data.length);
    } catch (error) {
      console.error('Failed to fetch feedback:', error);
    } finally {
      setLoading(false);
    }
  }, [docId, onFeedbackCountChange]);

  useEffect(() => {
    if (isOpen && docId) {
      fetchFeedback();
    }
  }, [isOpen, docId, fetchFeedback]);

  // Pre-fill section when opened from a heading button
  useEffect(() => {
    if (initialSection && isOpen) {
      setSectionHeading(initialSection);
      setShowForm(true);
    }
  }, [initialSection, isOpen]);

  const handleSubmit = async () => {
    if (!comment.trim()) {
      toast.error('Please enter a comment');
      return;
    }
    setSubmitting(true);
    try {
      await submitDocFeedback(docId, {
        section_heading: sectionHeading,
        type: feedbackType,
        comment: comment.trim(),
      });
      toast.success('Feedback submitted');
      setSectionHeading('');
      setComment('');
      setFeedbackType('general');
      setShowForm(false);
      fetchFeedback();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChange = async (feedbackId, newStatus) => {
    try {
      await updateFeedbackStatus(docId, feedbackId, newStatus);
      toast.success(`Feedback marked as ${newStatus}`);
      fetchFeedback();
    } catch (error) {
      console.error('Failed to update feedback status:', error);
      toast.error('Failed to update status');
    }
  };

  const selectedType = FEEDBACK_TYPES.find(t => t.value === feedbackType);
  const filteredFeedbacks = filterStatus === 'all'
    ? feedbacks
    : feedbacks.filter(f => f.status === filterStatus);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/20 z-40" onClick={onClose} />

      {/* Panel */}
      <div className="fixed right-0 top-0 h-full w-full sm:w-[420px] bg-white z-50 shadow-xl flex flex-col" data-testid="feedback-panel">
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageSquarePlus className="h-5 w-5 text-[#2B8FD8]" />
            <h3 className="font-semibold text-slate-900 text-base">Feedback</h3>
            {feedbacks.length > 0 && (
              <Badge variant="outline" className="text-[10px]">
                {feedbacks.filter(f => f.status === 'open').length} open
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            {feedbacks.some(f => f.status === 'open') && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleResolveWithAI}
                disabled={resolving}
                className="h-7 text-xs border-[#2B8FD8]/40 text-[#2B8FD8] hover:bg-blue-50"
                data-testid="resolve-feedback-ai-btn"
                title="Have AI draft a revision addressing all open feedback (you review before applying)"
              >
                {resolving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />}
                {resolving ? 'Drafting…' : 'Resolve with AI'}
              </Button>
            )}
            <Button
              size="sm"
              onClick={() => { setShowForm(true); setSectionHeading(''); setComment(''); setFeedbackType('general'); }}
              className="bg-[#2B8FD8] hover:bg-[#2075b3] h-7 text-xs"
              data-testid="new-feedback-btn"
            >
              <MessageSquarePlus className="h-3.5 w-3.5 mr-1" /> New
            </Button>
            <button onClick={onClose} className="p-1 rounded hover:bg-slate-100" data-testid="close-feedback-panel">
              <X className="h-4 w-4 text-slate-400" />
            </button>
          </div>
        </div>

        {/* AI proposal (propose-only — user must explicitly apply) */}
        {proposal && (
          <div className="border-b border-slate-200 p-4 bg-blue-50/40 space-y-2" data-testid="feedback-ai-proposal">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-[#2B8FD8]" />
              <p className="text-sm font-semibold text-slate-800">AI-proposed revision</p>
            </div>
            {proposal.explanation && (
              <p className="text-xs text-slate-600">{proposal.explanation}</p>
            )}
            <div className="max-h-48 overflow-y-auto rounded border border-slate-200 bg-white p-2 text-[11px] font-mono whitespace-pre-wrap text-slate-700">
              {(proposal.suggested_content || '').slice(0, 8000)}
            </div>
            <div className="flex items-center gap-2 pt-1">
              <Button size="sm" onClick={handleApplyProposal} disabled={applying}
                className="bg-[#2B8FD8] hover:bg-[#2075b3] h-7 text-xs" data-testid="apply-proposal-btn">
                {applying ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1" />}
                Apply revision
              </Button>
              <Button size="sm" variant="outline" onClick={() => setProposal(null)} className="h-7 text-xs" data-testid="dismiss-proposal-btn">
                Dismiss
              </Button>
            </div>
          </div>
        )}

        {/* New Feedback Form */}
        {showForm && (
          <div className="border-b border-slate-200 p-4 bg-slate-50/50 space-y-3" data-testid="feedback-form">
            {/* Section Selector */}
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Section</label>
              <select
                value={sectionHeading}
                onChange={(e) => setSectionHeading(e.target.value)}
                className="w-full h-8 rounded-md border border-slate-200 text-sm px-2 bg-white focus:ring-1 focus:ring-[#2B8FD8] focus:border-[#2B8FD8] outline-none"
                data-testid="feedback-section-select"
              >
                <option value="">General (entire document)</option>
                {sectionHeadings.map((h, i) => (
                  <option key={i} value={h}>{h}</option>
                ))}
              </select>
            </div>

            {/* Type Selector */}
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Type</label>
              <div className="relative">
                <button
                  onClick={() => setTypeDropdownOpen(!typeDropdownOpen)}
                  className={`w-full h-8 rounded-md border text-sm px-2 flex items-center justify-between bg-white ${selectedType?.bg || 'border-slate-200'}`}
                  data-testid="feedback-type-select"
                >
                  <span className="flex items-center gap-1.5">
                    {selectedType && <selectedType.icon className={`h-3.5 w-3.5 ${selectedType.color}`} />}
                    {selectedType?.label || 'Select type'}
                  </span>
                  <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
                </button>
                {typeDropdownOpen && (
                  <div className="absolute top-full left-0 w-full mt-1 bg-white rounded-md border border-slate-200 shadow-lg z-10 py-1">
                    {FEEDBACK_TYPES.map((t) => (
                      <button
                        key={t.value}
                        onClick={() => { setFeedbackType(t.value); setTypeDropdownOpen(false); }}
                        className="w-full px-3 py-1.5 text-sm text-left flex items-center gap-2 hover:bg-slate-50"
                      >
                        <t.icon className={`h-3.5 w-3.5 ${t.color}`} />
                        {t.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Comment */}
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Comment *</label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Describe the issue or suggestion..."
                rows={3}
                className="w-full rounded-md border border-slate-200 text-sm px-3 py-2 bg-white resize-none focus:ring-1 focus:ring-[#2B8FD8] focus:border-[#2B8FD8] outline-none"
                data-testid="feedback-comment-input"
              />
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between">
              <Button variant="ghost" size="sm" onClick={() => setShowForm(false)} className="text-xs text-slate-500 h-7">
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSubmit}
                disabled={submitting || !comment.trim()}
                className="bg-[#2B8FD8] hover:bg-[#2075b3] h-7 text-xs"
                data-testid="submit-feedback-btn"
              >
                {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Send className="h-3.5 w-3.5 mr-1" />}
                Submit
              </Button>
            </div>
          </div>
        )}

        {/* Filter tabs */}
        <div className="flex gap-1 px-4 pt-3 pb-2">
          {['all', 'open', 'acknowledged', 'resolved'].map((s) => (
            <button
              key={s}
              onClick={() => setFilterStatus(s)}
              className={`text-[11px] px-2 py-1 rounded-full font-medium transition-colors ${
                filterStatus === s
                  ? 'bg-[#2B8FD8] text-white'
                  : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
              }`}
            >
              {s === 'all' ? `All (${feedbacks.length})` : `${s.charAt(0).toUpperCase() + s.slice(1)} (${feedbacks.filter(f => f.status === s).length})`}
            </button>
          ))}
        </div>

        {/* Feedback List */}
        <div className="flex-1 overflow-y-auto px-4 pb-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-[#2B8FD8]" />
            </div>
          ) : filteredFeedbacks.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-400">
              <MessageSquarePlus className="h-10 w-10 mb-3" />
              <p className="text-sm font-medium">No feedback yet</p>
              <p className="text-xs mt-1">Submit feedback to improve AI-generated docs</p>
            </div>
          ) : (
            <div className="space-y-3 pt-1">
              {filteredFeedbacks.map((fb) => {
                const typeInfo = FEEDBACK_TYPES.find(t => t.value === fb.feedback_type) || FEEDBACK_TYPES[4];
                const statusInfo = STATUS_MAP[fb.status] || STATUS_MAP.open;
                return (
                  <div key={fb.id} className={`rounded-lg border p-3 ${typeInfo.bg}`} data-testid={`feedback-item-${fb.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <typeInfo.icon className={`h-3.5 w-3.5 shrink-0 ${typeInfo.color}`} />
                        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${typeInfo.badge}`}>{typeInfo.label}</span>
                        {fb.section_heading && (
                          <span className="text-[10px] text-slate-500 truncate">
                            in "{fb.section_heading}"
                          </span>
                        )}
                      </div>
                      <div className={`flex items-center gap-1 ${statusInfo.color} text-[10px] shrink-0`}>
                        <statusInfo.icon className="h-3 w-3" />
                        {statusInfo.label}
                      </div>
                    </div>
                    <p className="text-sm text-slate-700 mt-2 leading-relaxed">{fb.comment}</p>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-[10px] text-slate-400">
                        {fb.user_name} · {new Date(fb.created_at).toLocaleDateString()}
                      </span>
                      {fb.status === 'open' && (
                        <div className="flex gap-1">
                          <button
                            onClick={() => handleStatusChange(fb.id, 'acknowledged')}
                            className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 hover:bg-amber-200"
                          >
                            Acknowledge
                          </button>
                          <button
                            onClick={() => handleStatusChange(fb.id, 'resolved')}
                            className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                          >
                            Resolve
                          </button>
                        </div>
                      )}
                      {fb.status === 'acknowledged' && (
                        <button
                          onClick={() => handleStatusChange(fb.id, 'resolved')}
                          className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                        >
                          Resolve
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default FeedbackPanel;
